<template>
  <ResidentDetailView
    v-if="selectedResident"
    :resident="selectedResident"
    :clusters="clusters"
    @back="handleDetailBack"
    @updated="handleDetailUpdated"
  />

  <div
    v-else
    class="manager-residents-ant"
    :class="{ 'is-loading': loading }"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="manager-residents-ant__title">Резиденты</h2>

    <div class="manager-residents-ant__action-bar">
      <SearchInput v-model="searchQuery" placeholder="Найти..." @search="handleSearch" />

      <button type="button" class="btn btn-secondary btn-medium" @click="filterOpen = true">
        <FilterIcon :filled="isFilterApplied" />
        <span>Фильтр</span>
      </button>

      <button type="button" class="btn btn-primary btn-medium" @click="createDrawerOpen = true">
        <span>Создать резидента</span>
      </button>
    </div>

    <p
      v-if="hasActiveSearch"
      class="search-summary manager-residents-ant__search-summary"
      v-html="searchSummaryMarkup"
    />

    <div class="manager-residents-ant__table-wrapper personal-area-table-wrapper">
      <div v-if="shouldShowEmpty" class="manager-residents-ant__list--empty">
        <div class="manager-residents-ant__list--empty-title">{{ emptyState.title }}</div>
        <div
          v-if="emptyState.description"
          class="manager-residents-ant__list--empty-description"
        >
          {{ emptyState.description }}
        </div>
      </div>

      <a-table
        v-else-if="shouldShowTable"
        class="manager-residents-ant__table"
        :columns="columns"
        :bordered="true"
        :data-source="items"
        :loading="loading"
        :pagination="false"
        :scroll="TABLE_SCROLL"
        :custom-row="getRowProps"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'id'">
            {{ record.id }}
          </template>

          <template v-else-if="column.key === 'company'">
            {{ record.company }}
          </template>

          <template v-else-if="column.key === 'email'">
            <span class="manager-residents-ant__email">{{ record.email }}</span>
          </template>

          <template v-else-if="column.key === 'status'">
            <span
              class="manager-residents-ant__status"
              :class="`manager-residents-ant__status--${getStatusModifier(record.status)}`"
            >
              <span class="manager-residents-ant__status-dot" aria-hidden="true" />
              {{ record.status || '—' }}
            </span>
          </template>
        </template>
      </a-table>
    </div>

    <Pagination
      v-if="nav.totalCount > nav.pageSize"
      v-model="currentPage"
      class="manager-residents-ant__pagination personal-area-table-pagination"
      :total-count="nav.totalCount"
      :page-size="nav.pageSize"
      @change="handlePageChange"
    />

    <ResidentsFilterDrawer
      v-model:open="filterOpen"
      :clusters="clusters"
      :status-options="statusOptions"
      :filter="appliedFilter"
      @apply="handleFilterApply"
    />

    <CreateResidentDrawer
      v-model:open="createDrawerOpen"
      :clusters="clusters"
      :status-options="statusOptionsRaw"
      @created="handleResidentCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import FilterIcon from '@/js/components/icons/FilterIcon.vue';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import SearchInput from '../SearchInput.vue';
import Pagination from '../Pagination.vue';
import ResidentDetailView from './ResidentDetailView.vue';
import ResidentsFilterDrawer from './ResidentsFilterDrawer.vue';
import CreateResidentDrawer from './CreateResidentDrawer.vue';
import {
  fetchManagerResidentsFilter,
  fetchManagerResidentsList,
} from './manager-residents-api';
import type {
  ManagerResidentRow,
  ManagerResidentsClusterOption,
  ManagerResidentsFilter,
  ManagerResidentsNav,
  ManagerResidentStatusOption,
} from './types';

const SEARCH_MIN_LENGTH = 3;
const TABLE_SCROLL = { x: 1250, y: 500 };

const columns: TableColumnsType<ManagerResidentRow> = [
  {
    title: '#',
    key: 'id',
    dataIndex: 'id',
    width: 72,
  },
  {
    title: 'Компания',
    key: 'company',
    dataIndex: 'company',
    width: 220,
  },
  {
    title: 'Кластер',
    key: 'cluster',
    dataIndex: 'cluster',
    width: 180,
  },
  {
    title: 'Руководитель',
    key: 'manager',
    dataIndex: 'manager',
    width: 180,
  },
  {
    title: 'Телефон',
    key: 'phone',
    dataIndex: 'phone',
    width: 170,
  },
  {
    title: 'Почта',
    key: 'email',
    dataIndex: 'email',
    width: 200,
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 170,
  },
];

const loading = ref(false);
const errorMessage = ref('');
const items = ref<ManagerResidentRow[]>([]);
const clusters = ref<ManagerResidentsClusterOption[]>([]);
const nav = ref<ManagerResidentsNav>({
  totalCount: 0,
  pageSize: 10,
  currentPage: 1,
  pageCount: 1,
});
const searchQuery = ref('');
const appliedSearchQuery = ref('');
const currentPage = ref(1);
const filterOpen = ref(false);
const appliedFilter = ref<ManagerResidentsFilter>({});
const selectedResident = ref<ManagerResidentRow | null>(null);
const statusOptions = ref<{ value: string; label: string }[]>([]);
const statusOptionsRaw = ref<ManagerResidentStatusOption[]>([]);
const createDrawerOpen = ref(false);

const isFilterApplied = computed(() =>
  Boolean(appliedFilter.value.clusterId || appliedFilter.value.statusId),
);

const hasActiveSearch = computed(() => appliedSearchQuery.value.trim().length >= SEARCH_MIN_LENGTH);

const searchSummaryMarkup = computed(() =>
  formatMatchCountSummary(nav.value.totalCount, appliedSearchQuery.value.trim()),
);

const shouldShowTable = computed(() => loading.value || nav.value.totalCount > 0);

const shouldShowEmpty = computed(
  () => !loading.value && nav.value.totalCount === 0 && !hasActiveSearch.value,
);

const emptyState = computed(() => {
  if (errorMessage.value) {
    return {
      title: errorMessage.value,
      description: '',
    };
  }

  if (isFilterApplied.value) {
    return {
      title: '0 результатов',
      description: 'Измените значения в фильтре.',
    };
  }

  return {
    title: 'Резидентов нет',
    description: '',
  };
});

function getStatusModifier(statusName?: string): string {
  const normalized = (statusName ?? '').toLowerCase();

  if (normalized.includes('актив')) return 'active';
  if (normalized.includes('исключ')) return 'excluded';
  if (normalized.includes('приостан') || normalized.includes('рассмотр')) return 'suspended';

  return 'default';
}

async function loadResidents(page = currentPage.value, query = appliedSearchQuery.value) {
  loading.value = true;
  errorMessage.value = '';

  try {
    const data = await fetchManagerResidentsList({
      q: query,
      page,
      filter: appliedFilter.value,
    });

    items.value = data.items;
    nav.value = data.nav;
    clusters.value = data.clusters;
    currentPage.value = data.nav.currentPage;
  } catch (error) {
    items.value = [];
    nav.value = {
      totalCount: 0,
      pageSize: 10,
      currentPage: 1,
      pageCount: 1,
    };
    errorMessage.value =
      error instanceof Error ? error.message : 'Не удалось загрузить список резидентов';
  } finally {
    loading.value = false;
  }
}

function handleSearch(query: string) {
  appliedSearchQuery.value = query;
  currentPage.value = 1;
  void loadResidents(1, query);
}

function handlePageChange(page: number) {
  currentPage.value = page;
  void loadResidents(page);
}

function handleFilterApply(filter: ManagerResidentsFilter) {
  appliedFilter.value = filter;
  currentPage.value = 1;
  void loadResidents(1);
}

function getRowProps(record: ManagerResidentRow) {
  return {
    onClick: () => {
      selectedResident.value = record;
    },
  };
}

function handleDetailBack() {
  selectedResident.value = null;
}

function handleDetailUpdated() {
  void loadResidents(currentPage.value);
}

function handleResidentCreated() {
  void loadResidents(1);
}

onMounted(async () => {
  try {
    const filterData = await fetchManagerResidentsFilter();
    clusters.value = filterData.clusters;
    statusOptions.value = filterData.statuses.map((item) => ({
      value: item.id,
      label: item.name,
    }));
    statusOptionsRaw.value = filterData.statuses;
  } catch {
    statusOptions.value = [];
    statusOptionsRaw.value = [];
  }

  void loadResidents(1);
});
</script>
